import { createServerFn } from "@tanstack/react-start";
import { queryOptions } from "@tanstack/react-query";
import { z } from "zod";
import { requireAdmin } from "@/lib/admin/require-admin.server";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { dbError } from "@/lib/db";
import { listarArvoreCategorias, type TinyCategoriaNode } from "@/lib/erp/tiny-client.server";

export interface AdminCategory {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  image_url: string | null;
  sort_order: number;
  is_active: boolean;
  parent_id: string | null;
  tiny_category_id: string | null;
  created_at: string;
}

export const categoryInputSchema = z.object({
  name: z.string().min(2),
  slug: z.string().min(2),
  description: z.string().nullable(),
  imageUrl: z.string().nullable(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
  parentId: z.string().uuid().nullable(),
  tinyCategoryId: z.string().nullable(),
});
export type AdminCategoryInput = z.infer<typeof categoryInputSchema>;

// Handlers extraídos em funções simples: createServerFn lança "No Start
// context found" quando chamada fora de uma requisição real, então testar a
// const exportada diretamente quebra — mesmo motivo documentado em
// addresses.functions.ts.

function toCategoryFields(data: AdminCategoryInput) {
  return {
    name: data.name,
    slug: data.slug,
    description: data.description,
    image_url: data.imageUrl,
    sort_order: data.sortOrder,
    is_active: data.isActive,
    parent_id: data.parentId,
    tiny_category_id: data.tinyCategoryId,
  };
}

export async function listAllCategories(): Promise<AdminCategory[]> {
  const { data, error } = await supabaseAdmin.from("categories").select("*").order("sort_order");
  if (error) throw dbError(error, "src/lib/admin/categories.functions.ts");
  return (data ?? []) as AdminCategory[];
}

export async function getCategoryById(id: string): Promise<AdminCategory | null> {
  const { data, error } = await supabaseAdmin
    .from("categories")
    .select("*")
    .eq("id", id)
    .maybeSingle();
  if (error) throw dbError(error, "src/lib/admin/categories.functions.ts");
  return (data ?? null) as AdminCategory | null;
}

// Só duas camadas (categoria > subcategoria): impede um terceiro nível para
// manter o filtro da loja simples (chip de categoria expande subcategorias,
// não teria como expandir "netas"). Checagem em TS, não trigger: não é uma
// invariante crítica onde uma corrida concorrente causaria dano real.
async function assertMaxTwoLevels(parentId: string | null): Promise<void> {
  if (!parentId) return;
  const { data: parent, error } = await supabaseAdmin
    .from("categories")
    .select("parent_id")
    .eq("id", parentId)
    .maybeSingle();
  if (error) throw dbError(error, "src/lib/admin/categories.functions.ts");
  if (parent?.parent_id) {
    throw new Error("Só é permitido um nível de subcategoria (categoria > subcategoria).");
  }
}

export async function insertCategory(data: AdminCategoryInput): Promise<{ id: string }> {
  await assertMaxTwoLevels(data.parentId);
  const { data: row, error } = await supabaseAdmin
    .from("categories")
    .insert(toCategoryFields(data))
    .select("id")
    .single();
  if (error) throw dbError(error, "src/lib/admin/categories.functions.ts");
  return { id: row.id };
}

export async function editCategory(data: AdminCategoryInput & { id: string }): Promise<void> {
  const { id, ...rest } = data;
  if (rest.parentId === id) {
    throw new Error("Uma categoria não pode ser subcategoria de si mesma.");
  }
  await assertMaxTwoLevels(rest.parentId);
  const { error } = await supabaseAdmin
    .from("categories")
    .update(toCategoryFields(rest))
    .eq("id", id);
  if (error) throw dbError(error, "src/lib/admin/categories.functions.ts");
}

export async function removeCategory(id: string): Promise<void> {
  const { count: childCount, error: childError } = await supabaseAdmin
    .from("categories")
    .select("id", { count: "exact", head: true })
    .eq("parent_id", id);
  if (childError) throw dbError(childError, "src/lib/admin/categories.functions.ts");
  if ((childCount ?? 0) > 0) {
    throw new Error("Remova ou mova as subcategorias antes de excluir esta categoria.");
  }

  const { count: productCount, error: productError } = await supabaseAdmin
    .from("products")
    .select("id", { count: "exact", head: true })
    .eq("category_id", id);
  if (productError) throw dbError(productError, "src/lib/admin/categories.functions.ts");
  if ((productCount ?? 0) > 0) {
    throw new Error("Existem produtos nesta categoria. Mova-os antes de excluir.");
  }

  const { error } = await supabaseAdmin.from("categories").delete().eq("id", id);
  if (error) throw dbError(error, "src/lib/admin/categories.functions.ts");
}

// Busca a árvore do Tiny para popular o seletor "Categoria no Tiny" do
// formulário. Best-effort simples (sem retry): se a integração não estiver
// conectada ou a chamada falhar, devolve [] e o formulário fica sem opções
// nesse campo — o admin ainda salva a categoria local sem vínculo.
export async function listTinyCategoriesFlat(): Promise<{ id: string; label: string }[]> {
  let tree: TinyCategoriaNode[];
  try {
    tree = await listarArvoreCategorias();
  } catch (error) {
    console.error("[categories] Falha ao buscar árvore de categorias do Tiny", error);
    return [];
  }
  const flat: { id: string; label: string }[] = [];
  const walk = (nodes: TinyCategoriaNode[], depth: number) => {
    for (const node of nodes) {
      flat.push({ id: String(node.id), label: `${"— ".repeat(depth)}${node.descricao}` });
      if (node.filhas?.length) walk(node.filhas, depth + 1);
    }
  };
  walk(tree, 0);
  return flat;
}

export const adminListCategories = createServerFn({ method: "GET" })
  .middleware([requireAdmin])
  .handler(() => listAllCategories());

export const adminGetCategory = createServerFn({ method: "GET" })
  .middleware([requireAdmin])
  .validator((id: string) => z.string().uuid().parse(id))
  .handler(async ({ data: id }) => getCategoryById(id));

export const adminCreateCategory = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .validator((data: AdminCategoryInput) => categoryInputSchema.parse(data))
  .handler(async ({ data }) => insertCategory(data));

export const adminUpdateCategory = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .validator((data: AdminCategoryInput & { id: string }) =>
    categoryInputSchema.extend({ id: z.string().uuid() }).parse(data),
  )
  .handler(async ({ data }) => editCategory(data));

export const adminDeleteCategory = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .validator((id: string) => z.string().uuid().parse(id))
  .handler(async ({ data: id }) => removeCategory(id));

export const adminListTinyCategories = createServerFn({ method: "GET" })
  .middleware([requireAdmin])
  .handler(() => listTinyCategoriesFlat());

export const adminCategoriesQuery = () =>
  queryOptions({ queryKey: ["admin", "categories"], queryFn: () => adminListCategories() });

export const adminCategoryQuery = (id: string) =>
  queryOptions({
    queryKey: ["admin", "category", id],
    queryFn: () => adminGetCategory({ data: id }),
  });

export const adminTinyCategoriesQuery = () =>
  queryOptions({
    queryKey: ["admin", "tiny", "categories"],
    queryFn: () => adminListTinyCategories(),
  });
